<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import {
    CONSENT_REQUEST_STATUS,
    CONSENT_REQUEST_TYPE,
    PCE_DDA_STATUS,
} from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { useSourceStore } from "@core/stores/source.store"
import { coreServiceRegistry } from "@core/services/register"
import { formatAddress, formatIdSource } from "@core/helpers"
import type { CompanyType, OwnerOutput } from "@core/composables"
import type {
    ConsentRequestInvitationDto,
    SourceConsentSignBuilding,
    SourceConsentSignRequestProps,
    SourceConsentSignSource,
} from "@core/types/source/consent_request"

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    request: ConsentRequestInvitationDto
}>()

const emit = defineEmits<{
    done: []
}>()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Mise à jour de l’accès aux données",
            message_sign:
                "{name} vous êtes sur le point de signer les consentements nécessaires pour autoriser l’accès aux données du compteur de gaz numéro {idsource}{addressSuffix}",
            timeline_step_verify: "Vérification des informations",
            timeline_step_sign: "Signature du consentement",
            timeline_step_grdf: "Déclaration GRDF",
            dda_active_can_leave: "Vous pouvez quitter cette page.",
            grdf_declaration_leave_hint:
                "Si la déclaration auprès de GRDF ne fonctionne pas ou reste bloquée, vous pouvez quitter cette page.",
        },
    },
})

// ================================
// STORES
// ================================

const authStore = useAuthStore()
const configStore = useConfigStore()
const sourceStore = useSourceStore()

// ================================
// STEP FLOW (intro → summary → sign → grdf)
// ================================

type Step = "intro" | "summary" | "sign" | "grdf"

const step = ref<Step>("intro")
const ctxDate = ref("")
const ctxZipcode = ref("")
const ctxSourceId = ref("")
const ctxConsentId = ref("")

// ================================
// COMPUTED
// ================================

const getCustomer = computed(() => !props.request.user_to?.company?.name)

const getOwner = computed((): OwnerOutput => {
    const u = props.request.user_to
    return {
        user: {
            _id: u?._id ?? undefined,
            firstname: u?.firstname ?? "",
            lastname: u?.lastname ?? "",
            username: (u?.username ?? "").toLowerCase(),
        },
        company: u?.company?.name
            ? {
                  _id: u.company._id ?? "",
                  name: u.company.name,
                  type:
                      (u.company.type as CompanyType | undefined) ??
                      "entreprise",
                  address: null,
              }
            : null,
    }
})

const summaryOwnerPayload = computed(() => ({
    user: {
        firstname: props.request.user_to?.firstname,
        lastname: props.request.user_to?.lastname,
        username: props.request.user_to?.username,
    },
    company: props.request.user_to?.company
        ? {
              name: props.request.user_to.company.name,
              type: props.request.user_to.company.type,
          }
        : null,
}))

const getBuilding = computed(
    (): SourceConsentSignBuilding => ({
        _id_building: props.request.building?._id ?? "",
        address: {
            street: props.request.address?.street ?? "",
            zipcode: ctxZipcode.value || props.request.address?.zipcode || "",
            city: props.request.address?.city ?? "",
            country: props.request.address?.country ?? "",
            citycode: props.request.address?.citycode ?? null,
        },
    })
)

const getSource = computed(
    (): SourceConsentSignSource => ({
        idsource: props.request.source?.idsource ?? "",
        idtype: "pce",
        date_from_data: ctxDate.value || undefined,
    })
)

const getConsentDurationMonths = computed(() => {
    const fromOp = props.request.user_from?.company?.operation?.consent_duration
    if (fromOp != null) return fromOp
    const scopeDuration = (
        configStore.scope as { consentement?: { duration?: number } } | null
    )?.consentement?.duration
    if (scopeDuration != null) return scopeDuration / 12
    return 60
})

const consentSignType = computed(() => {
    if (props.request.type === CONSENT_REQUEST_TYPE.CHANGE_OWNER)
        return CONSENT_REQUEST_TYPE.CHANGE_OWNER
    return CONSENT_REQUEST_TYPE.SIGN
})

const consentSignRequest = computed(
    (): SourceConsentSignRequestProps => ({
        _id_consentrequest: props.request._id,
        type: consentSignType.value,
        mail: true,
        duration: getConsentDurationMonths.value,
    })
)

const dateFromForSign = computed(() => {
    const d = ctxDate.value
    if (d) return d.slice(0, 10)
    return new Date().toISOString().slice(0, 10)
})

const headerMessage = computed(() => {
    const u = props.request.user_to
    const name = `${u?.firstname ?? ""} ${u?.lastname ?? ""}`.trim()
    const idsource = props.request.source?.idsource
        ? formatIdSource(props.request.source.idsource, "pce")
        : ""
    const addr = props.request.address
        ? formatAddress(props.request.address)
        : ""
    const addressSuffix = addr ? ` situé au ${addr}.` : "."
    return t("message_sign", { name, idsource, addressSuffix })
})

/** Affiche la timeline PrimeVue dès l’étape « vérification » (hors intro plein écran). */
const showInvitationTimeline = computed(() => step.value !== "intro")

/** Index de l’étape courante parmi les 3 étapes métier (0 = vérification, 1 = signature, 2 = GRDF). */
const invitationTimelineActiveIndex = computed(() => {
    if (step.value === "summary") return 0
    if (step.value === "sign") return 1
    if (step.value === "grdf") return 2
    return -1
})

type InvitationTimelineStatus = "completed" | "current" | "disabled"

/**
 * Marqueurs : même logique visuelle que {@link MeterCardInvitationSign}
 * (vertical par défaut, slots marker + opposite + content).
 */
const invitationTimelineMarkerClass: Record<InvitationTimelineStatus, string> =
    {
        completed: "border-green-500 bg-green-500",
        current: "border-orange-500 bg-orange-500",
        disabled: "border-gray-300 bg-gray-100",
    }

type InvitationTimelineItem = {
    label: string
    status: InvitationTimelineStatus
    stepNumber: number
    markerClass: string
    showCheck: boolean
    numberClass: string
}

/** DDA GRDF validée (Active) : message de sortie du parcours invitation. */
const isDdaActive = computed(
    () =>
        sourceStore.getSourceById(ctxSourceId.value)?.informations.pce
            ?.droit_access?.etat === PCE_DDA_STATUS.ACTIVE
)

const invitationTimelineItems = computed((): InvitationTimelineItem[] => {
    const labels = [
        t("timeline_step_verify"),
        t("timeline_step_sign"),
        t("timeline_step_grdf"),
    ] as const
    const current = invitationTimelineActiveIndex.value
    if (current < 0) return []
    return labels.map((label, index) => {
        let status: InvitationTimelineStatus
        if (index < current) status = "completed"
        else if (index === current) status = "current"
        else status = "disabled"
        const stepNumber = index + 1
        return {
            label,
            status,
            stepNumber,
            markerClass: invitationTimelineMarkerClass[status],
            showCheck: status === "completed",
            numberClass: status === "current" ? "text-white" : "text-gray-600",
        }
    })
})

// ================================
// LIFECYCLE
// ================================

onMounted(() => {
    ctxDate.value =
        props.request.consentement?.date_from_data ??
        props.request.consentement?.date_from ??
        ""
    ctxZipcode.value = props.request.address?.zipcode ?? ""
    ctxSourceId.value = props.request.source?._id ?? ""
    ctxConsentId.value = props.request.consentement?._id ?? ""

    const st = props.request.state
    if (
        st === CONSENT_REQUEST_STATUS.WAITING_GRDF ||
        st === CONSENT_REQUEST_STATUS.ACCEPTED
    ) {
        step.value = "grdf"
    }
})

watch(
    () => props.request.consentement?.date_from_data,
    (v) => {
        if (v && step.value === "intro") ctxDate.value = v
    }
)

// ================================
// ACTIONS
// ================================

function onIntroNext(): void {
    step.value = "summary"
}

function onSummaryQuit(): void {
    step.value = "intro"
}

function onSummaryUpdate(payload: { date: string; zipcode: string }): void {
    ctxDate.value = payload.date
    ctxZipcode.value = payload.zipcode
}

function onSummaryDone(): void {
    step.value = "sign"
}

function onSignBack(): void {
    step.value = "summary"
}

async function onSigned(payload: {
    consentId?: string
    sourceId?: string
    token?: string
}): Promise<void> {
    if (payload.token) {
        authStore.setToken(payload.token)
        await authStore.fetchUser()
    }
    if (payload.sourceId) ctxSourceId.value = payload.sourceId
    if (payload.consentId) ctxConsentId.value = payload.consentId
    step.value = "grdf"
}

async function onGrdfDone(): Promise<void> {
    const rid = props.request._id
    if (!rid) {
        emit("done")
        return
    }
    try {
        if (
            props.request.type === CONSENT_REQUEST_TYPE.CHANGE_OWNER &&
            props.request.state === CONSENT_REQUEST_STATUS.WAITING_GRDF
        ) {
            await coreServiceRegistry.consentRequest.updateConsentRequestChangeOwner(
                rid,
                CONSENT_REQUEST_STATUS.TERMINATED,
                { mail: true }
            )
        } else {
            await coreServiceRegistry.consentRequest.updateConsentRequestSign(
                rid
            )
        }
    } catch {
        /* aligné legacy : ne bloque pas la fermeture */
    }
    emit("done")
}
</script>

<template>
    <div
        class="pce-form-on-invitation flex flex-col items-center gap-8 w-full max-w-3xl mx-auto"
    >
        <header class="flex flex-col items-center gap-4 w-full pt-6">
            <h1 class="text-xl font-semibold text-gray-800 text-center">
                {{ t("title") }}
            </h1>
            <p class="text-sm text-gray-700 text-center max-w-2xl">
                {{ headerMessage }}
            </p>
            <BaseTimeline
                v-if="showInvitationTimeline && invitationTimelineItems.length"
                :value="invitationTimelineItems"
                data-key="stepNumber"
                class="pce-invitation-timeline w-full max-w-2xl"
                layout="horizontal"
                align="top"
            >
                <template #marker="{ item }">
                    <span
                        :class="[
                            'w-8 h-8 flex items-center justify-center rounded-full border shrink-0 text-sm font-medium',
                            item.markerClass,
                        ]"
                    >
                        <i
                            v-if="item.showCheck"
                            class="pi pi-check text-white block"
                            style="font-size: 0.75rem"
                        />
                        <span
                            v-else
                            class="leading-none"
                            :class="item.numberClass"
                        >
                            {{ item.stepNumber }}
                        </span>
                    </span>
                </template>
                <template #opposite="{ item }">
                    <span
                        class="text-sm font-medium text-gray-800 whitespace-nowrap"
                    >
                        {{ item.label }}
                    </span>
                </template>
            </BaseTimeline>
        </header>

        <PceFormOnInvitationIntro
            v-if="step === 'intro'"
            :customer="getCustomer"
            @click="onIntroNext"
        />

        <PceFormOnInvitationSummary
            v-else-if="
                step === 'summary' && request._id && request.source?.idsource
            "
            :consent-request-id="request._id"
            :idsource="request.source.idsource"
            :initial-zipcode="ctxZipcode"
            :initial-date="ctxDate"
            :owner="summaryOwnerPayload"
            @update:request="onSummaryUpdate"
            @done="onSummaryDone"
            @click:quit="onSummaryQuit"
        />

        <SourceConsentSign
            v-else-if="
                step === 'sign' && request._id && request.source?.idsource
            "
            :owner="getOwner"
            :source="getSource"
            :building="getBuilding"
            :date-from="dateFromForSign"
            :request="consentSignRequest"
            class="w-full"
            @back="onSignBack"
            @signed="onSigned"
            hide-back-button
            hide-header-title
        />

        <div
            v-else-if="step === 'grdf' && ctxSourceId && ctxConsentId"
            class="flex flex-col gap-4 w-full"
        >
            <BaseMessage severity="info" class="w-full" :closable="false">
                {{ t("grdf_declaration_leave_hint") }}
            </BaseMessage>
            <PceFormGrdfDeclaration
                :meter-id="ctxSourceId"
                :id-consent="ctxConsentId"
                hide-terminer
                @done="onGrdfDone"
            />
            <p
                v-if="isDdaActive"
                class="w-full text-center text-gray-600 text-sm"
            >
                {{ t("dda_active_can_leave") }}
            </p>
        </div>
    </div>
</template>

<style scoped>
/**
 * Timeline horizontale : libellé au-dessus du marker, centrés sur la colonne d’étape
 * (PrimeVue place opposite puis separator en column flex sur chaque event).
 */
.pce-invitation-timeline :deep(.p-timeline-horizontal .p-timeline-event) {
    align-items: center;
}

.pce-invitation-timeline
    :deep(.p-timeline-horizontal .p-timeline-event-opposite) {
    width: 100%;
    text-align: center;
}

.pce-invitation-timeline
    :deep(.p-timeline-horizontal .p-timeline-event-separator) {
    justify-content: center;
}

.pce-invitation-timeline
    :deep(.p-timeline-horizontal .p-timeline-event-content) {
    width: 100%;
    text-align: center;
}
</style>
